import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';

export { MAX_ASSET_BASE, maxAsset, maxAssetUrl } from './assets';

export {
  getMaxPlanMonths,
  getMaxPlanTerms,
  getMaxYearlySavePercent,
  getMaxYearlySaving,
  type MaxPlanTerms,
  type MaxYearlySaving,
} from './plan';

export {
  MAX_CHIP_KEYS,
  MAX_VISIBLE_CHIP_FALLBACK,
  getMaxChipsForRegion,
  getMaxCycleLabelKey,
  getMaxPowerPlugCount,
  isMaxChipAvailable,
  type MaxChipKey,
} from './features';

export type MaxPlanKey = 'monthly' | 'yearly';

export interface MaxPlan {
  /** gid://shopify/ProductVariant/… — monthly and yearly are separate products. */
  variantId: string;
  /** gid://shopify/SellingPlan/… from the Subscriptions app; absent = one-time purchase (IN). */
  sellingPlanId?: string;
  /** Price of one billing cycle in the store currency. */
  price: number;
  /**
   * Billing-cycle length in months. Defaults to 1 for `monthly` and 12 for
   * `yearly`; IN's short plan is a 3-month one-time purchase sold under the
   * `monthly` key, so every label/suffix must go through `getMaxPlanTerms`.
   */
  months?: number;
}

export interface MaxStoreConfig {
  plans: Record<MaxPlanKey, MaxPlan>;
  /** Overrides the computed yearly saving badge (subscription stores). */
  savePercent?: number;
  /**
   * Plans are bought once, never billed (IN). Drives every surface through
   * `getMaxPlanTerms`: "3 months" / "1 year" labels, no price suffix, a rupee
   * saving badge, a one-time cart bullet, no per-month figures, no HSA badge.
   */
  oneTime?: boolean;
}

export const MAX_PLAN_KEYS: MaxPlanKey[] = ['yearly', 'monthly'];
export const MAX_DEFAULT_PLAN: MaxPlanKey = 'yearly';

/** Buy-funnel step state: plan chosen, `false` declined, `null` unanswered (blocks the CTA). */
export type MaxSelection = MaxPlanKey | false | null;
export const hasMaxSelected = (
  selection: MaxSelection | undefined,
): selection is MaxPlanKey => selection === 'monthly' || selection === 'yearly';

const PRODUCTION = process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION';

export const getMaxVariantIds = (cfg: MaxStoreConfig): string[] =>
  MAX_PLAN_KEYS.map((key) => cfg.plans[key].variantId);

export const isMaxVariantId = (
  variantId: string | null | undefined,
  store: ShopifyStore | string | null | undefined,
): boolean => {
  if (!variantId) return false;
  const cfg = getMaxConfig(store);
  return cfg ? getMaxVariantIds(cfg).includes(variantId) : false;
};

// Staging: every store resolves to uhtesting. The variant is the POC product
// "uhx-membership-subscription-poc"; the selling plans must be created in
// Apps → Subscriptions and pasted here before add-to-cart works on staging.
const STAGING_VARIANT = 'gid://shopify/ProductVariant/56120797003849';
const staging = (
  monthly: number,
  yearly: number,
  opts: { monthlyMonths?: number; oneTime?: boolean } = {},
): MaxStoreConfig => ({
  ...(opts.oneTime ? { oneTime: true } : {}),
  plans: {
    monthly: {
      variantId: STAGING_VARIANT,
      sellingPlanId: 'gid://shopify/SellingPlan/PLACEHOLDER_MONTHLY',
      price: monthly,
      ...(opts.monthlyMonths !== undefined
        ? { months: opts.monthlyMonths }
        : {}),
    },
    yearly: {
      // One-time stores (IN) sell two products. Distinct ids let the
      // variant fallback in resolveMaxPlanKey be tested without production.
      variantId: opts.oneTime
        ? 'gid://shopify/ProductVariant/56120797003850'
        : STAGING_VARIANT,
      sellingPlanId: 'gid://shopify/SellingPlan/PLACEHOLDER_YEARLY',
      price: yearly,
    },
  },
});

// Display prices are the Storefront presentment price of each plan variant
// (`@inContext(country:)` on the shop that serves that store, 2026-10-19).
// EU/GB/AU/JP/SA/ZA/MX share variant GIDs; the amount is per market.
// SG and BR are on the old ops sheet (SGD / BRL) but COUNTRY_STORE_CONFIG
// still maps them to ROW + USD, so they inherit ROW.
// A store without an entry shows no MAX anywhere (funnel step, cart rail,
// upsell, shop). IN is a one-time purchase (no selling plan) whose short plan
// covers 3 months; every other store is a monthly/yearly subscription. ROW is
// the ultrahuman-us (global) store.
const gid = (id: string) => `gid://shopify/ProductVariant/${id}`;
const plan = (
  variant: string,
  price: number,
  opts: { sellingPlanId?: string; months?: number } = {},
): MaxPlan => ({
  variantId: gid(variant),
  price,
  ...(opts.sellingPlanId !== undefined
    ? { sellingPlanId: opts.sellingPlanId }
    : {}),
  ...(opts.months !== undefined ? { months: opts.months } : {}),
});
const EU_UK = { monthly: '58596692230518', yearly: '58596692590966' };
// CA shares the US backend (StoreAliasGroups.US_CA): same variants and selling
// plans, different presentment currency.
const US_CA = { monthly: '43115963711584', yearly: '43115963777120' };
const usCa = (monthly: number, yearly: number): MaxStoreConfig => ({
  plans: {
    monthly: plan(US_CA.monthly, monthly, {
      sellingPlanId: 'gid://shopify/SellingPlan/2051342432',
    }),
    yearly: plan(US_CA.yearly, yearly, {
      sellingPlanId: 'gid://shopify/SellingPlan/2051375200',
    }),
  },
});
const euUk = (monthly: number, yearly: number): MaxStoreConfig => ({
  plans: {
    monthly: plan(EU_UK.monthly, monthly, {
      sellingPlanId: 'gid://shopify/SellingPlan/712995864950',
    }),
    yearly: plan(EU_UK.yearly, yearly, {
      sellingPlanId: 'gid://shopify/SellingPlan/712996553078',
    }),
  },
});

const PRODUCTION_MAP: Partial<Record<ShopifyStore, MaxStoreConfig>> = {
  [ShopifyStore.US]: usCa(9.99, 99.99),
  [ShopifyStore.CA]: usCa(12.99, 129.99),
  [ShopifyStore.IN]: {
    // One-time purchases: 3 months for ₹2,399 (under the `monthly` key) and 12
    // months for ₹7,999. Not ₹799/mo — that SKU does not exist.
    // Shopify: 3 months = Product/8529426808902, 1 year = Product/8529426841670.
    oneTime: true,
    plans: {
      monthly: plan('46489786515526', 2399, { months: 3 }),
      yearly: plan('46489786548294', 7999),
    },
  },
  [ShopifyStore.AE]: {
    plans: {
      monthly: plan('64595911049587', 39.99, {
        sellingPlanId: 'gid://shopify/SellingPlan/693058011507',
      }),
      yearly: plan('64595911082355', 399.99, {
        sellingPlanId: 'gid://shopify/SellingPlan/693058044275',
      }),
    },
  },
  [ShopifyStore.ROW]: {
    plans: {
      monthly: plan('50501791547452', 9.99, {
        sellingPlanId: 'gid://shopify/SellingPlan/2532606012',
      }),
      yearly: plan('50501791612988', 99.99, {
        sellingPlanId: 'gid://shopify/SellingPlan/2532638780',
      }),
    },
  },
  [ShopifyStore.EU]: euUk(11.95, 119.95),
  [ShopifyStore.GB]: euUk(9.99, 99.99),
  [ShopifyStore.AU]: euUk(20, 200),
  [ShopifyStore.JP]: euUk(2200, 21600),
  [ShopifyStore.SA]: euUk(51, 511),
  [ShopifyStore.ZA]: euUk(224, 2242),
  [ShopifyStore.MX]: euUk(252, 2519),
};

export const MaxShopifyMap: Partial<Record<ShopifyStore, MaxStoreConfig>> =
  PRODUCTION
    ? PRODUCTION_MAP
    : {
        [ShopifyStore.US]: staging(9.99, 99.99),
        [ShopifyStore.IN]: staging(2399, 7999, {
          monthlyMonths: 3,
          oneTime: true,
        }),
        [ShopifyStore.GB]: staging(9.99, 99.99),
        [ShopifyStore.EU]: staging(11.95, 119.95),
        [ShopifyStore.AE]: staging(39.99, 399.99),
        [ShopifyStore.CA]: staging(12.99, 129.99),
        [ShopifyStore.AU]: staging(20, 200),
        [ShopifyStore.JP]: staging(2200, 21600),
        [ShopifyStore.ROW]: staging(9.99, 99.99),
        [ShopifyStore.SA]: staging(51, 511),
        [ShopifyStore.ZA]: staging(224, 2242),
        [ShopifyStore.MX]: staging(252, 2519),
      };

export const getMaxConfig = (
  store: ShopifyStore | string | null | undefined,
): MaxStoreConfig | undefined =>
  store ? MaxShopifyMap[store as ShopifyStore] : undefined;

export const isMaxAvailable = (
  store: ShopifyStore | string | null | undefined,
): boolean => Boolean(getMaxConfig(store));

/** The one add-to-cart shape for MAX, shared by funnel, cart rail, upsell modal and /shop. */
export function buildMaxLineItem(
  store: ShopifyStore | string | null | undefined,
  planKey: MaxPlanKey,
  opts: { funnel?: string } = {},
): addToCartArgsType | null {
  const cfg = getMaxConfig(store);
  if (!cfg) return null;
  const maxPlan = cfg.plans[planKey];
  return {
    shopifyVariantId: maxPlan.variantId,
    productType: ProductType.MAX_SUBSCRIPTION,
    ...(maxPlan.sellingPlanId ? { sellingPlanId: maxPlan.sellingPlanId } : {}),
    params: {
      maxQuantity: 1,
      noteAttributes: opts.funnel
        ? [{ key: '_funnel', value: opts.funnel }]
        : [],
    },
  };
}

/** Which plan a cart line is: by selling plan id, then variant (OTP lines), then plan name. */
export function resolveMaxPlanKey(
  sellingPlan: { id?: string | null; name?: string | null } | null | undefined,
  store: ShopifyStore | string | null | undefined,
  variantId?: string | null,
): MaxPlanKey | null {
  const cfg = getMaxConfig(store);
  if (cfg && sellingPlan?.id) {
    for (const key of MAX_PLAN_KEYS) {
      if (cfg.plans[key].sellingPlanId === sellingPlan.id) return key;
    }
  }
  // Staging shares one variant across both plans, so only match distinct variants.
  if (
    cfg &&
    variantId &&
    cfg.plans.monthly.variantId !== cfg.plans.yearly.variantId
  ) {
    for (const key of MAX_PLAN_KEYS) {
      if (cfg.plans[key].variantId === variantId) return key;
    }
  }
  if (!sellingPlan?.id) return null;
  const name = sellingPlan.name?.toLowerCase() ?? '';
  if (/year|annual/.test(name)) return 'yearly';
  if (/month/.test(name)) return 'monthly';
  return null;
}
